import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Shared selection state for left applications menu + right account chip app icon.
 *
 * @param {Array<{ id: string, active?: boolean }>} applications
 * @param {string | undefined} selectedApplicationIdProp
 * @param {(application: object) => void} [onSelectApplication]
 */
export function useTopbarApplicationSelection(
  applications = [],
  selectedApplicationIdProp,
  onSelectApplication,
) {
  const isControlled = selectedApplicationIdProp !== undefined;
  const initialSelectedApplicationId = useMemo(
    () =>
      applications.find((application) => application.active)?.id ||
      applications[0]?.id ||
      null,
    [applications],
  );
  const [uncontrolledSelectedApplicationId, setUncontrolledSelectedApplicationId] = useState(
    initialSelectedApplicationId,
  );

  const selectedApplicationId = isControlled
    ? selectedApplicationIdProp
    : uncontrolledSelectedApplicationId;

  useEffect(() => {
    if (isControlled) return;

    if (!applications.length) {
      setUncontrolledSelectedApplicationId(null);
      return;
    }

    setUncontrolledSelectedApplicationId((currentId) => {
      const hasCurrentSelection = applications.some(
        (application) => application.id === currentId,
      );
      return hasCurrentSelection ? currentId : initialSelectedApplicationId;
    });
  }, [applications, isControlled, initialSelectedApplicationId]);

  const selectedApplication = useMemo(
    () => applications.find((application) => application.id === selectedApplicationId) ?? null,
    [applications, selectedApplicationId],
  );

  const handleSelectApplication = useCallback(
    (application) => {
      if (!isControlled) {
        setUncontrolledSelectedApplicationId(application.id);
      }
      onSelectApplication?.(application);
      application.onSelect?.(application);
    },
    [isControlled, onSelectApplication],
  );

  return {
    selectedApplicationId,
    selectedApplication,
    handleSelectApplication,
  };
}
